import {
  HELP_IMAGES_MAX,
  HELP_INBOX_REPORT_FIELD,
  HELP_INBOX_SCREENSHOT_FIELD,
  helpInboxApiPaths,
  helpInboxCountSchema,
  helpInboxListResponseSchema,
  helpInboxReportDetailSchema,
  helpInboxReportSummarySchema,
  type HelpImageInput,
  type HelpInboxCount,
  type HelpInboxReportCreate,
  type HelpInboxReportDetail,
  type HelpInboxReportSummary,
  type HelpInboxStatus,
} from "@ttc-spectree/toolbox-contract/help";
import { api } from "./client";

/**
 * Client for the SpecTree Toolbox Help inbox (epic PI-SPEC-SW-E302, feature
 * PI-SPEC-SW-1559, ER-1655 Resolved Decision 5). Routes under
 * `/toolbox/help-inbox` (contract `HELP_INBOX_PATH`):
 * - `POST   /`                        one confirmed "Send to Aaron" report (multipart:
 *                                     the `report` JSON plus 0-3 `screenshot` files)
 * - `GET    /?status=open|handled|all` the inbox, newest first (global Admins)
 * - `GET    /count`                   open reports, for the rail badge (global Admins)
 * - `GET    /:id`                     one report (global Admins)
 * - `GET    /:id/screenshots/:index`  one screenshot's bytes (global Admins)
 * - `PATCH  /:id`                     mark handled or open again (global Admins)
 *
 * Everything goes through the ApiClient (auth headers, token refresh, error
 * shaping); the multipart POST hands it a FormData body so the browser sets
 * the boundary, as the FogBugz screenshot upload does. Responses are parsed
 * with the lenient contract schemas. Screenshots leave the computer only
 * inside a report the person confirmed.
 */

interface DataResponse<T> {
  data: T;
}

/** The api client's base is `/api/v1`; the contract paths are absolute. */
const API_PREFIX = "/api/v1";

function relative(path: string): string {
  return path.startsWith(API_PREFIX) ? path.slice(API_PREFIX.length) : path;
}

/** Decode a screenshot's base64 bytes into a Blob of its type. Browser-safe. */
export function helpImageBlob(image: HelpImageInput): Blob {
  const binary = atob(image.dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: image.mimeType });
}

/**
 * The multipart body of `POST /toolbox/help-inbox`: the `report` JSON field
 * first, then one `screenshot` file per image (at most HELP_IMAGES_MAX; the
 * caller refuses more before it gets here). Pure; exported for tests.
 */
export function helpInboxFormData(report: HelpInboxReportCreate, images: readonly HelpImageInput[]): FormData {
  if (images.length > HELP_IMAGES_MAX) {
    throw new Error(`A report may carry at most ${String(HELP_IMAGES_MAX)} screenshots`);
  }
  const form = new FormData();
  form.append(HELP_INBOX_REPORT_FIELD, JSON.stringify(report));
  for (const image of images) {
    // A File (not a bare Blob) so every FormData implementation keeps the name.
    form.append(HELP_INBOX_SCREENSHOT_FIELD, new File([helpImageBlob(image)], image.name, { type: image.mimeType }));
  }
  return form;
}

/** The ApiClient path of one stored screenshot (for `getBlob`; the route needs the Bearer token). */
export function helpInboxScreenshotPath(id: string, index: number): string {
  return relative(helpInboxApiPaths.screenshot(id, index));
}

export const helpInboxApi = {
  /** Store one confirmed report. One call is one report; a refusal stores nothing. */
  create: async (report: HelpInboxReportCreate, images: readonly HelpImageInput[]): Promise<HelpInboxReportSummary> => {
    const response = await api.request<DataResponse<unknown>>(relative(helpInboxApiPaths.create), {
      method: "POST",
      body: helpInboxFormData(report, images),
    });
    return helpInboxReportSummarySchema.parse(response.data);
  },

  /** The inbox, newest first. */
  list: async (status: HelpInboxStatus = "open"): Promise<HelpInboxReportSummary[]> => {
    const response = await api.get<DataResponse<unknown>>(relative(helpInboxApiPaths.list(status)));
    return helpInboxListResponseSchema.parse(response.data).reports;
  },

  /** How many reports are open. */
  count: async (): Promise<HelpInboxCount> => {
    const response = await api.get<DataResponse<unknown>>(relative(helpInboxApiPaths.count));
    return helpInboxCountSchema.parse(response.data);
  },

  /** One whole report. */
  get: async (id: string): Promise<HelpInboxReportDetail> => {
    const response = await api.get<DataResponse<unknown>>(relative(helpInboxApiPaths.report(id)));
    return helpInboxReportDetailSchema.parse(response.data);
  },

  /** One screenshot's bytes. */
  screenshot: (id: string, index: number): Promise<Blob> => api.getBlob(helpInboxScreenshotPath(id, index)),

  /** Mark a report handled (`true`) or open again (`false`). */
  setHandled: async (id: string, handled: boolean): Promise<HelpInboxReportSummary> => {
    const response = await api.patch<DataResponse<unknown>>(relative(helpInboxApiPaths.report(id)), { handled });
    return helpInboxReportSummarySchema.parse(response.data);
  },
};

export type { HelpInboxCount, HelpInboxReportDetail, HelpInboxReportSummary, HelpInboxStatus };
